import React, { useCallback, useEffect, useState } from 'react';
import { formatAmount, money } from '../../lib/money';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Badge,
  Banner,
  Button,
  Card,
  ConfirmDialog,
  DescriptionList,
  EmptyState,
  Input,
  KpiTile,
  PageContainer,
  PageHeader,
  Spinner,
  Textarea,
  Timeline,
  useToast,
} from '../../ui/components';
import { Icon } from '../../ui/icons';
import { useProperty } from '../../app/PropertyContext';
import { useT } from '../../i18n';
import { catalogApi } from '../catalog/catalogApi';
import ApprovalChain from '../approvals/ApprovalChain';
import AttachmentsCard from '../attachments/AttachmentsCard';
import CommentComposer from '../comments/CommentComposer';
import { buildCommentTimelineItem, useCommentAnchor } from '../comments/CommentTimeline';
import { useAuth } from '../../auth/AuthContext';
import { invoicePath, purchaseOrderPath } from '../../lib/slug';
import { invoicesApi, invoiceStatusMeta, matchStatusMeta } from './invoicesApi';
import '../requisitions/requisitions.css';
import './invoices.css';

const num = (v) => { const n = Number(v); return Number.isNaN(n) ? 0 : n; };
const signed = (n, cur) => `${n > 0 ? '+' : ''}${cur ? `${cur} ` : ''}${formatAmount(n)}`;

const ACTIVITY_META = {
  Created: { tone: 'brand', icon: <Icon.FileText size={14} /> },
  Updated: { tone: 'default', icon: <Icon.Pencil size={14} /> },
  Approved: { tone: 'success', icon: <Icon.CircleCheck size={14} /> },
  Disputed: { tone: 'warning', icon: <Icon.TriangleAlert size={14} /> },
  Paid: { tone: 'brand', icon: <Icon.Wallet size={14} /> },
  Cancelled: { tone: 'danger', icon: <Icon.Ban size={14} /> },
};

export default function InvoiceDetailPage() {
  const { id: routeRef } = useParams();
  const navigate = useNavigate();
  const t = useT();
  const toast = useToast();
  const { hasPermission, canActOnStepRole } = useProperty();
  const { user } = useAuth();
  const canManage = hasPermission('invoices.manage');
  const canApprove = hasPermission('invoices.approve');

  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [units, setUnits] = useState([]);
  const [activity, setActivity] = useState([]);
  const [editingCommentId, setEditingCommentId] = useState(null);
  const anchorId = useCommentAnchor(activity);
  const [busy, setBusy] = useState(false);

  const [approveOpen, setApproveOpen] = useState(false);
  const [approveComment, setApproveComment] = useState('');
  const [disputeOpen, setDisputeOpen] = useState(false);
  const [disputeReason, setDisputeReason] = useState('');
  const [payOpen, setPayOpen] = useState(false);
  const [payRef, setPayRef] = useState('');
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  // Inline edit state (draft / disputed only).
  const [editing, setEditing] = useState(false);
  const [draftLines, setDraftLines] = useState([]);
  const [supplierInvoiceNumber, setSupplierInvoiceNumber] = useState('');
  const [tax, setTax] = useState('');
  const [freight, setFreight] = useState('');
  const [other, setOther] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setInvoice(await invoicesApi.get(routeRef));
    } catch (err) {
      setError(err?.message || t('Invoice not found.'));
    } finally {
      setLoading(false);
    }
  }, [routeRef, t]);

  const loadActivity = useCallback(async (id) => {
    try {
      const res = await invoicesApi.listActivity(id, { pageSize: 100 });
      setActivity(res.items || []);
    } catch { /* non-fatal */ }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (invoice?.id) loadActivity(invoice.id); }, [invoice?.id, loadActivity]);

  useEffect(() => {
    let active = true;
    catalogApi.listUnits().then((u) => { if (active) setUnits(u || []); }).catch(() => {});
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (!invoice?.id) return;
    const canonical = invoicePath(invoice);
    if (`/invoices/${routeRef}` !== canonical) navigate(canonical, { replace: true });
  }, [invoice, routeRef, navigate]);

  const uomCode = (uid) => units.find((u) => u.id === uid)?.code;
  const currency = invoice?.currency || 'USD';

  const beginEdit = () => {
    setDraftLines((invoice.lines || []).map((l) => ({
      poLineId: l.purchaseOrderLineId,
      qty: String(l.invoicedQuantity),
      price: String(l.invoiceUnitPrice?.amount ?? 0),
    })));
    setSupplierInvoiceNumber(invoice.supplierInvoiceNumber || '');
    setTax(invoice.taxTotal?.amount ? String(invoice.taxTotal.amount) : '');
    setFreight(invoice.freightAmount?.amount ? String(invoice.freightAmount.amount) : '');
    setOther(invoice.otherCharges?.amount ? String(invoice.otherCharges.amount) : '');
    setEditing(true);
  };

  const saveEdit = async () => {
    setBusy(true);
    try {
      const saved = await invoicesApi.save(invoice.id, {
        supplierInvoiceNumber: supplierInvoiceNumber.trim() || null,
        invoiceDate: invoice.invoiceDate,
        dueDate: invoice.dueDate,
        notes: invoice.notes,
        taxTotal: num(tax),
        freightAmount: num(freight),
        otherCharges: num(other),
        lines: draftLines
          .filter((l) => num(l.qty) > 0)
          .map((l) => ({ purchaseOrderLineId: l.poLineId, invoicedQuantity: num(l.qty), invoiceUnitPrice: num(l.price), notes: null })),
      });
      setInvoice(saved);
      if (saved?.id) loadActivity(saved.id);
      setEditing(false);
      toast.success(t('Invoice updated and re-matched'));
    } catch (err) {
      toast.error(t('Could not save invoice'), { description: err?.message });
    } finally {
      setBusy(false);
    }
  };

  const run = async (fn, okMsg, errMsg, after) => {
    setBusy(true);
    try {
      const res = await fn();
      setInvoice(res);
      if (res?.id) loadActivity(res.id);
      after?.();
      toast.success(okMsg);
    } catch (err) {
      toast.error(errMsg, { description: err?.message });
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <PageContainer size="full"><div style={{ display: 'grid', placeItems: 'center', padding: 'var(--space-16)' }}><Spinner size={26} /></div></PageContainer>;
  }
  if (error || !invoice) {
    return (
      <PageContainer size="full">
        <EmptyState icon={<Icon.CircleAlert size={28} />} title={t("Couldn't load invoice")} description={error || t('Not found.')}
          action={<Button variant="secondary" onClick={() => navigate('/invoices')}>{t('Back to invoices')}</Button>} />
      </PageContainer>
    );
  }

  const statusMeta = invoiceStatusMeta(invoice.status);
  const matchMeta = matchStatusMeta(invoice.matchStatus);
  const isDraft = invoice.status === 'Draft';
  const isPendingApproval = invoice.status === 'PendingApproval';
  const isDisputed = invoice.status === 'Disputed';
  const isApproved = invoice.status === 'Approved';
  const isPaid = invoice.status === 'Paid';
  const isCancelled = invoice.status === 'Cancelled';
  const editable = (isDraft || isDisputed) && canManage;

  const steps = invoice.approvalSteps || [];
  const hasChain = steps.length > 0;
  const currentStep = hasChain ? steps.find((s) => s.order === invoice.currentApprovalStepOrder) : null;
  // Draft/disputed has no chain yet (it's built on first approval); pending requires the step's role.
  const canApproveNow = canApprove && (
    isDraft || isDisputed || (isPendingApproval && (!currentStep || canActOnStepRole(currentStep.roleId)))
  );
  const approveLabel = isPendingApproval ? t('Approve step {order}', { order: currentStep?.order ?? '' }).trim() : t('Approve');

  const headerActions = editing ? null : (
    <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
      {editable && (
        <Button variant="secondary" size="sm" leadingIcon={<Icon.Pencil size={14} />} onClick={beginEdit}>{t('Edit')}</Button>
      )}
      {canApproveNow && (
        <Button variant="primary" size="sm" leadingIcon={<Icon.CircleCheck size={14} />} onClick={() => setApproveOpen(true)}>{approveLabel}</Button>
      )}
      {(isDraft || isPendingApproval || isApproved) && canManage && (
        <Button variant="secondary" size="sm" leadingIcon={<Icon.TriangleAlert size={14} />} onClick={() => setDisputeOpen(true)}>{t('Dispute')}</Button>
      )}
      {isApproved && canManage && (
        <Button variant="primary" size="sm" leadingIcon={<Icon.Wallet size={14} />} onClick={() => setPayOpen(true)}>{t('Mark paid')}</Button>
      )}
      {!isPaid && !isCancelled && canManage && (
        <Button variant="destructive" size="sm" leadingIcon={<Icon.Ban size={14} />} onClick={() => setCancelOpen(true)}>{t('Cancel')}</Button>
      )}
    </div>
  );

  const overviewItems = [
    { term: t('Status'), description: <Badge tone={statusMeta.tone} dot>{t(statusMeta.label)}</Badge> },
    { term: t('Match'), description: <Badge tone={matchMeta.tone}>{t(matchMeta.label)}</Badge> },
    { term: t('Supplier'), description: invoice.supplierName || '—' },
    {
      term: t('Purchase order'),
      description: invoice.purchaseOrderNumber
        ? <button type="button" onClick={() => navigate(purchaseOrderPath({ number: invoice.purchaseOrderNumber }))} style={{ fontFamily: 'var(--font-mono)', background: 'none', border: 'none', padding: 0, color: 'var(--color-brand, #2563eb)', cursor: 'pointer' }}>{invoice.purchaseOrderNumber}</button>
        : '—',
    },
    { term: t('Supplier invoice #'), description: invoice.supplierInvoiceNumber || '—' },
    { term: t('Invoice date'), description: invoice.invoiceDate ? new Date(invoice.invoiceDate).toLocaleDateString() : '—' },
    { term: t('Due date'), description: invoice.dueDate ? new Date(invoice.dueDate).toLocaleDateString() : '—' },
    { term: t('Created by'), description: invoice.createdByName || '—' },
  ];
  if (isApproved || isPaid) {
    overviewItems.push({ term: t('Approved by'), description: `${invoice.approvedByName || '—'}${invoice.approvedAt ? ` · ${new Date(invoice.approvedAt).toLocaleDateString()}` : ''}` });
    if (invoice.decisionComment) overviewItems.push({ term: t('Approval note'), description: invoice.decisionComment });
  }
  if (isPaid) {
    overviewItems.push({ term: t('Paid by'), description: `${invoice.paidByName || '—'}${invoice.paidAt ? ` · ${new Date(invoice.paidAt).toLocaleDateString()}` : ''}` });
    if (invoice.paymentReference) overviewItems.push({ term: t('Payment ref'), description: invoice.paymentReference });
  }

  const setLine = (poLineId, patch) =>
    setDraftLines((prev) => prev.map((l) => (l.poLineId === poLineId ? { ...l, ...patch } : l)));

  const editSubtotal = draftLines.filter((l) => num(l.qty) > 0).reduce((a, l) => a + num(l.qty) * num(l.price), 0);
  const editTotal = editSubtotal + num(tax) + num(freight) + num(other);
  const fmt = (n) => `${currency} ${formatAmount(n)}`;

  const renderMatchLine = (l) => {
    const lm = matchStatusMeta(l.matchStatus);
    const uom = uomCode(l.uomId) ? ` ${uomCode(l.uomId)}` : '';
    return (
      <div className={`inv-line ${l.withinTolerance ? '' : 'is-variance'}`} key={l.lineId}>
        <div className="inv-line__item">
          <div className="inv-line__name">{l.description}</div>
          <div className="inv-line__sub">{l.sku ? `${l.sku} · ` : ''}<Badge tone={lm.tone}>{t(lm.label)}</Badge></div>
        </div>
        <div className="inv-line__stat">
          <span className="inv-line__stat-label">{t('Ordered / received / billed')}</span>
          <span className="inv-line__stat-value">{l.orderedQuantity} / {l.receivedQuantity} / {l.invoicedQuantity}{uom}</span>
          {Math.abs(l.quantityVariance) > 0.0001 && <span className={`inv-line__delta ${l.quantityVariance > 0 ? 'is-up' : 'is-down'}`}>{t('{delta} vs received', { delta: signed(l.quantityVariance) })}</span>}
        </div>
        <div className="inv-line__stat">
          <span className="inv-line__stat-label">{t('PO price')}</span>
          <span className="inv-line__stat-value">{money(l.poUnitPrice)}</span>
        </div>
        <div className="inv-line__stat">
          <span className="inv-line__stat-label">{t('Billed price')}</span>
          <span className="inv-line__stat-value">{money(l.invoiceUnitPrice)}</span>
          {Math.abs(l.priceVariance) > 0.0001 && <span className={`inv-line__delta ${l.priceVariance > 0 ? 'is-up' : 'is-down'}`}>{t('{delta} vs PO', { delta: signed(l.priceVariance) })}</span>}
        </div>
        <div className="inv-line__stat" style={{ alignItems: 'flex-end' }}>
          <span className="inv-line__stat-label">{t('Line total')}</span>
          <span className="inv-line__stat-value">{money(l.lineTotal)}</span>
        </div>
      </div>
    );
  };

  const renderEditLine = (l) => {
    const src = invoice.lines.find((x) => x.purchaseOrderLineId === l.poLineId) || {};
    const uom = uomCode(src.uomId) ? ` ${uomCode(src.uomId)}` : '';
    return (
      <div className="inv-line" key={l.poLineId}>
        <div className="inv-line__item">
          <div className="inv-line__name">{src.description}</div>
          <div className="inv-line__sub">{src.sku ? `${src.sku} · ` : ''}{t('Received')} {src.receivedQuantity}{uom} · PO {money(src.poUnitPrice)}</div>
        </div>
        <div className="inv-line__stat">
          <span className="inv-line__stat-label">{t('Billed qty')}</span>
          <Input type="number" min="0" step="any" value={l.qty} onChange={(e) => setLine(l.poLineId, { qty: e.target.value })} />
        </div>
        <div className="inv-line__stat">
          <span className="inv-line__stat-label">{t('PO price')}</span>
          <span className="inv-line__stat-value">{money(src.poUnitPrice)}</span>
        </div>
        <div className="inv-line__stat">
          <span className="inv-line__stat-label">{t('Billed price')}</span>
          <Input type="number" min="0" step="any" value={l.price} onChange={(e) => setLine(l.poLineId, { price: e.target.value })} />
        </div>
        <div className="inv-line__stat" style={{ alignItems: 'flex-end' }}>
          <span className="inv-line__stat-label">{t('Line total')}</span>
          <span className="inv-line__stat-value">{fmt(num(l.qty) * num(l.price))}</span>
        </div>
      </div>
    );
  };

  const activityItems = activity.map((a) => {
    if (a.type === 'Commented') {
      return buildCommentTimelineItem({
        activity: a,
        apiBasePath: `/supplier-invoices/${invoice.id}`,
        attachmentsBasePath: `/supplier-invoices/${invoice.id}/attachments`,
        currentUserId: user?.id,
        editingCommentId,
        setEditingCommentId,
        onChanged: () => loadActivity(invoice.id),
        highlightId: anchorId,
        t,
      });
    }

    const meta = ACTIVITY_META[a.type] || { tone: 'default', icon: <Icon.Activity size={14} /> };
    const detail = [
      a.metadata?.match && `${t('Match')}: ${t(matchStatusMeta(a.metadata.match).label)}`,
      a.metadata?.total && `${t('Total')} ${a.metadata.total}`,
      a.metadata?.budgetVariance && a.metadata.budgetVariance !== `${currency} 0.00` && `${t('Budget')} Δ ${a.metadata.budgetVariance}`,
      a.metadata?.paymentReference && t('Ref {number}', { number: a.metadata.paymentReference }),
      a.metadata?.reason && t('Reason: {reason}', { reason: a.metadata.reason }),
      a.metadata?.comment && `“${a.metadata.comment}”`,
    ].filter(Boolean).join(' · ');
    return {
      id: a.id,
      tone: meta.tone,
      icon: meta.icon,
      title: `${a.actorName || t('Someone')} ${a.summary}`,
      meta: new Date(a.createdAt).toLocaleString(),
      description: detail || undefined,
    };
  });

  return (
    <PageContainer size="full">
      <Button variant="ghost" size="sm" leadingIcon={<Icon.ArrowLeft size={15} />} onClick={() => navigate('/invoices')} style={{ alignSelf: 'flex-start', marginBottom: 'var(--space-3)' }}>
        {t('Invoices')}
      </Button>

      <PageHeader
        title={t('Invoice {number}', { number: invoice.number })}
        description={<span className="req-detail__number">{invoice.supplierName || t('Supplier')}{invoice.purchaseOrderNumber ? ` · ${invoice.purchaseOrderNumber}` : ''}</span>}
        icon={<Icon.Receipt size={22} />}
        actions={headerActions}
      />

      {invoice.budgetWarning && (isApproved || isPaid) && (
        <div style={{ marginBottom: 'var(--space-4)' }}>
          <Banner tone="warning">{t('Budget')}: {invoice.budgetWarning}</Banner>
        </div>
      )}
      {isPendingApproval && (
        <div style={{ marginBottom: 'var(--space-4)' }}>
          <Banner tone="info">
            {t('In approval — step {order} of {total}', { order: currentStep?.order ?? '?', total: steps.length })}
            {currentStep?.roleName ? t(', awaiting {role}', { role: currentStep.roleName }) : ''}.
            {invoice.hasVariance ? ` ${t('This invoice has a variance, so the full approval chain is required.')}` : ''}
            {canApproveNow ? ` ${t('You can approve this step.')}` : ''}
          </Banner>
        </div>
      )}
      {isDisputed && (
        <div style={{ marginBottom: 'var(--space-4)' }}>
          <Banner tone="warning">
            {t('Disputed')}{invoice.disputedByName ? ` ${t('by {name}', { name: invoice.disputedByName })}` : ''}{invoice.disputedAt ? ` ${t('on {date}', { date: new Date(invoice.disputedAt).toLocaleString() })}` : ''}.{invoice.disputeReason ? ` ${t('Reason: {reason}', { reason: invoice.disputeReason })}` : ''} {t('Edit the billed values and re-approve, or cancel.')}
          </Banner>
        </div>
      )}
      {isCancelled && (
        <div style={{ marginBottom: 'var(--space-4)' }}>
          <Banner tone="danger">
            {t('Cancelled')}{invoice.cancelledByName ? ` ${t('by {name}', { name: invoice.cancelledByName })}` : ''}{invoice.cancelledAt ? ` ${t('on {date}', { date: new Date(invoice.cancelledAt).toLocaleString() })}` : ''}.{invoice.cancelledReason ? ` ${t('Reason: {reason}', { reason: invoice.cancelledReason })}` : ''}
          </Banner>
        </div>
      )}

      <div className="req-kpis">
        <KpiTile label={t('Invoice total')} value={money(invoice.total)} />
        <KpiTile label={t('Match')} value={t(matchMeta.label)} meta={invoice.hasVariance ? t('Variance flagged') : t('Clean')} />
        <KpiTile label={t('Price variance')} value={money(invoice.priceVarianceTotal)} />
        <KpiTile label={t('Variance lines')} value={invoice.quantityVarianceCount + invoice.priceVarianceCount} />
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
        <Card>
          <Card.Body>
            <DescriptionList items={overviewItems} layout="stacked" columns={3} />
          </Card.Body>
        </Card>

        {editing ? (
          <Card>
            <Card.Header>
              <Card.Title>{t('Edit billed lines')}</Card.Title>
              <div className="pp-cell-muted" style={{ fontSize: 'var(--text-sm)', marginTop: 2 }}>{t('Adjust quantities and prices to what the supplier billed, then save to re-run the match.')}</div>
            </Card.Header>
            <Card.Body>
              <div style={{ marginBottom: 'var(--space-4)', maxWidth: 320 }}>
                <Input label={t('Supplier invoice #')} value={supplierInvoiceNumber} onChange={(e) => setSupplierInvoiceNumber(e.target.value)} placeholder={t('Their reference')} />
              </div>
              <div className="inv-lines">{draftLines.map(renderEditLine)}</div>
              <div className="inv-charges" style={{ margin: 'var(--space-4) 0' }}>
                <Input label={t('Tax ({currency})', { currency })} type="number" min="0" step="any" value={tax} onChange={(e) => setTax(e.target.value)} placeholder="0.00" />
                <Input label={t('Freight ({currency})', { currency })} type="number" min="0" step="any" value={freight} onChange={(e) => setFreight(e.target.value)} placeholder="0.00" />
                <Input label={t('Other ({currency})', { currency })} type="number" min="0" step="any" value={other} onChange={(e) => setOther(e.target.value)} placeholder="0.00" />
              </div>
              <div className="inv-totals">
                <div className="inv-totals__row"><span className="pp-cell-muted">{t('Subtotal')}</span><span>{fmt(editSubtotal)}</span></div>
                <div className="inv-totals__row is-grand"><span>{t('Total')}</span><span>{fmt(editTotal)}</span></div>
              </div>
              <div style={{ display: 'flex', gap: 'var(--space-2)', marginTop: 'var(--space-4)' }}>
                <Button variant="primary" loading={busy} leadingIcon={<Icon.Check size={16} />} onClick={saveEdit}>{t('Save changes')}</Button>
                <Button variant="ghost" onClick={() => setEditing(false)} disabled={busy}>{t('Cancel')}</Button>
              </div>
            </Card.Body>
          </Card>
        ) : (
          <Card>
            <Card.Header>
              <Card.Title>{t('Three-way match')}</Card.Title>
              <div className="pp-cell-muted" style={{ fontSize: 'var(--text-sm)', marginTop: 2 }}>
                {t('Each line compares the quantity billed against what was received, and the billed price against the purchase order.')}
              </div>
            </Card.Header>
            <Card.Body>
              <div className="inv-lines">{(invoice.lines || []).map(renderMatchLine)}</div>
              <div className="inv-totals" style={{ marginTop: 'var(--space-4)' }}>
                <div className="inv-totals__row"><span className="pp-cell-muted">{t('Subtotal')}</span><span>{money(invoice.subtotal)}</span></div>
                <div className="inv-totals__row"><span className="pp-cell-muted">{t('Tax')}</span><span>{money(invoice.taxTotal)}</span></div>
                <div className="inv-totals__row"><span className="pp-cell-muted">{t('Freight')}</span><span>{money(invoice.freightAmount)}</span></div>
                <div className="inv-totals__row"><span className="pp-cell-muted">{t('Other')}</span><span>{money(invoice.otherCharges)}</span></div>
                <div className="inv-totals__row is-grand"><span>{t('Total')}</span><span>{money(invoice.total)}</span></div>
              </div>
            </Card.Body>
          </Card>
        )}

        {invoice.notes && !editing && (
          <Card>
            <Card.Header><Card.Title>{t('Notes')}</Card.Title></Card.Header>
            <Card.Body>{invoice.notes}</Card.Body>
          </Card>
        )}

        {hasChain && (
          <Card>
            <Card.Header>
              <Card.Title>{t('Approval chain')}</Card.Title>
              <div className="pp-cell-muted" style={{ fontSize: 'var(--text-sm)', marginTop: 2 }}>
                {t('Follows the supplier-invoice approval workflow — amount-tiered approver roles. A variance requires the full chain.')}
              </div>
            </Card.Header>
            <Card.Body>
              <ApprovalChain
                steps={steps}
                currentStepOrder={invoice.currentApprovalStepOrder}
                currency={currency}
                showSignatures
              />
            </Card.Body>
          </Card>
        )}

        <AttachmentsCard basePath={`/supplier-invoices/${invoice.id}/attachments`} canManage={canManage} />

        <Card>
          <Card.Header>
            <Card.Title>{t('Activity')}</Card.Title>
            <div className="pp-cell-muted" style={{ fontSize: 'var(--text-sm)', marginTop: 2 }}>
              {t('Audit trail for this invoice — who did what and when.')}
            </div>
          </Card.Header>
          <Card.Body>
            <CommentComposer
              basePath={`/supplier-invoices/${invoice.id}`}
              onPosted={() => loadActivity(invoice.id)}
            />
            {activityItems.length === 0 ? (
              <EmptyState icon={<Icon.Activity size={26} />} title={t('No activity yet')} description={t('Capture, approval, dispute, payment and cancellation events will appear here.')} />
            ) : (
              <Timeline items={activityItems} className="req-activity__timeline" />
            )}
          </Card.Body>
        </Card>
      </div>

      <ConfirmDialog
        open={approveOpen}
        title={isPendingApproval ? t('Approve step {order}', { order: currentStep?.order ?? '' }).trim() : t('Approve invoice')}
        description={isPendingApproval
          ? t('Approve your step of the chain. The invoice advances to the next approver, or is fully approved (and posts its variance to budget) once the final step clears.')
          : t('This routes the invoice through the purchase-order approval chain (a variance requires the full chain). When the final step clears, the invoice is approved and its price/quantity variance is posted to budget actuals.')}
        confirmLabel={isPendingApproval ? t('Approve step') : t('Approve')}
        tone="primary"
        loading={busy}
        onConfirm={() => run(() => invoicesApi.approve(invoice.id, approveComment.trim() || null), t('Invoice approved'), t('Could not approve invoice'), () => { setApproveOpen(false); setApproveComment(''); })}
        onCancel={() => (busy ? null : setApproveOpen(false))}
      >
        <Textarea rows={2} value={approveComment} onChange={(e) => setApproveComment(e.target.value)} placeholder={t('Comment (optional)')} />
      </ConfirmDialog>

      <ConfirmDialog
        open={disputeOpen}
        title={t('Dispute invoice')}
        description={t("Hold the invoice back over a variance. An approved invoice's budget posting is withdrawn. You can edit and re-approve later.")}
        confirmLabel={t('Mark disputed')}
        tone="primary"
        loading={busy}
        onConfirm={() => run(() => invoicesApi.dispute(invoice.id, disputeReason.trim() || null), t('Invoice disputed'), t('Could not dispute invoice'), () => { setDisputeOpen(false); setDisputeReason(''); })}
        onCancel={() => (busy ? null : setDisputeOpen(false))}
      >
        <Textarea rows={2} value={disputeReason} onChange={(e) => setDisputeReason(e.target.value)} placeholder={t("What's the issue? (optional)")} />
      </ConfirmDialog>

      <ConfirmDialog
        open={payOpen}
        title={t('Mark invoice paid')}
        description={t('Record that this approved invoice has been settled.')}
        confirmLabel={t('Mark paid')}
        tone="primary"
        loading={busy}
        onConfirm={() => run(() => invoicesApi.markPaid(invoice.id, { paymentReference: payRef.trim() || null }), t('Invoice marked paid'), t('Could not mark invoice paid'), () => { setPayOpen(false); setPayRef(''); })}
        onCancel={() => (busy ? null : setPayOpen(false))}
      >
        <Input value={payRef} onChange={(e) => setPayRef(e.target.value)} placeholder={t('Payment reference (optional)')} />
      </ConfirmDialog>

      <ConfirmDialog
        open={cancelOpen}
        title={t('Cancel invoice')}
        description={t('Void this invoice. If it was approved, its budget posting is withdrawn.')}
        confirmLabel={t('Cancel invoice')}
        tone="danger"
        loading={busy}
        onConfirm={() => run(() => invoicesApi.cancel(invoice.id, cancelReason.trim() || null), t('Invoice cancelled'), t('Could not cancel invoice'), () => { setCancelOpen(false); setCancelReason(''); })}
        onCancel={() => (busy ? null : setCancelOpen(false))}
      >
        <Textarea rows={2} value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} placeholder={t('Reason (optional)')} />
      </ConfirmDialog>
    </PageContainer>
  );
}
